import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Checkbox,
  Collapse,
  Descriptions,
  Input,
  Segmented,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { BgColorsOutlined, UploadOutlined } from '@ant-design/icons';
import {
  asFontFile,
  draftTheme,
  getPublishedTheme,
  getThemeVocabulary,
  listThemeVersions,
  PALETTE_FIELDS,
  publishTheme,
  revertTheme,
  THEME_ASSET_MAX_BYTES,
  THEME_ASSET_MEDIA_TYPES,
  THEME_LAYOUTS,
  uploadThemeAsset,
} from '@/api/theme';
import type { THEME_ASSET_KINDS, ThemeVocabulary } from '@/api/theme';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import type { ThemeRequest, ThemeVersionView, ThemeView } from '@/api/types';
import { EmptyState } from '@/components/EmptyState';
import { formatAbsolute } from '@/lib/time';

const { Title, Paragraph, Text } = Typography;

/** Status with glyph + colour (never colour-only). */
function StatusTag({ status }: { status: string }) {
  const color = status === 'PUBLISHED' ? 'green' : status === 'DRAFT' ? 'blue' : undefined;
  return (
    <Tag color={color} bordered={false} style={{ margin: 0, fontSize: 11 }}>
      {status}
    </Tag>
  );
}

/** A small colour swatch preview for a token value — console-side hint only, the room is the
 *  authoritative render. Invalid values simply show no colour (the server validates for real). */
function Swatch({ value }: { value: string }) {
  if (!value.trim()) return null;
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 16,
        height: 16,
        borderRadius: 4,
        border: '1px solid var(--ol-border)',
        background: value,
        verticalAlign: 'middle',
      }}
    />
  );
}

type TokenMode = 'both' | 'light' | 'dark';

/**
 * Branding / Theme (OPSFL-THIAM-1) — the /v1/theme* staff surface, honestly scoped to what the
 * API accepts: layout variant (CLASSIC/COMPACT/SPLIT), the CLOSED 20-field Palette token set
 * (flat, or per light/dark mode), sandboxed custom CSS (G23: closed property allowlist —
 * url()/@import/at-rules/comments are refused with a precise 422), the closed brand key set
 * (product name, footer line, mark/wordmark/favicon assets, email header), and brand-asset
 * upload (≤1 MiB, png/svg/jpeg). No locales, no copy — those are not on this API.
 *
 * Ledger flow: draft (a new version) → publish (freeze + go live; ONE live theme per hub) →
 * revert (re-activate a frozen published version, or bare-revert to un-themed). theme.read
 * shows all of this; theme.write (hub_admin, not a step-up lane) is enforced server-side.
 */
export function ThemePanel() {
  const { message, modal } = App.useApp();

  const [live, setLive] = useState<ThemeView | null>(null);
  const [versions, setVersions] = useState<ThemeVersionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [forbidden, setForbidden] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [theme, ledger] = await Promise.all([getPublishedTheme(), listThemeVersions()]);
      setLive(theme);
      setVersions(ledger);
      setUnavailable(false);
      setForbidden(false);
    } catch (e) {
      const n = normalizeApiError(e);
      if (n.httpStatus === 403) setForbidden(true);
      else if (isNotAvailable(n)) setUnavailable(true);
      else message.error(n.message);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void load();
  }, [load]);

  const publish = useCallback(
    (version: number) => {
      modal.confirm({
        title: `Publish theme version ${version}?`,
        icon: null,
        okText: 'Publish',
        content:
          'Publishing freezes the version (content-hashed, immutable) and makes it the one live ' +
          'theme for this hub — the end-customer data room and branded emails pick it up ' +
          'immediately. The previous live version is superseded but stays revertible.',
        onOk: async () => {
          try {
            await publishTheme(version);
            message.success(`Version ${version} is live.`);
            await load();
          } catch (e) {
            message.error(normalizeApiError(e).message);
            throw e;
          }
        },
      });
    },
    [load, message, modal],
  );

  const revert = useCallback(
    (toVersion: number | null) => {
      modal.confirm({
        title: toVersion == null ? 'Remove the live theme?' : `Revert to version ${toVersion}?`,
        icon: null,
        okText: toVersion == null ? 'Un-theme' : 'Revert',
        okButtonProps: toVersion == null ? { danger: true } : undefined,
        content:
          toVersion == null
            ? 'The hub returns to the built-in platform identity (an honest default, not an error). Nothing is deleted — every version stays in the ledger.'
            : 'What you revert to is exactly what was live before: published versions are frozen, so no edits can have crept in.',
        onOk: async () => {
          try {
            await revertTheme(toVersion);
            message.success(toVersion == null ? 'Hub un-themed.' : `Version ${toVersion} is live again.`);
            await load();
          } catch (e) {
            message.error(normalizeApiError(e).message);
            throw e;
          }
        },
      });
    },
    [load, message, modal],
  );

  const columns: ColumnsType<ThemeVersionView> = useMemo(
    () => [
      { title: 'Version', dataIndex: 'version', key: 'version', width: 90 },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (v: string) => <StatusTag status={v} />,
      },
      { title: 'Layout', dataIndex: 'layoutVariant', key: 'layoutVariant', width: 110 },
      {
        title: 'Created',
        key: 'created',
        render: (_, r) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {formatAbsolute(r.createdAt)} {r.createdBy ? `· ${r.createdBy}` : ''}
          </Text>
        ),
      },
      {
        title: 'Published',
        key: 'published',
        render: (_, r) =>
          r.publishedAt ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {formatAbsolute(r.publishedAt)} {r.publishedBy ? `· ${r.publishedBy}` : ''}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 170,
        render: (_, r) => (
          <Space size={2}>
            {r.status === 'DRAFT' && (
              <Button type="link" size="small" style={{ padding: '0 6px' }} onClick={() => publish(r.version)}>
                Publish
              </Button>
            )}
            {r.status === 'SUPERSEDED' && r.publishedAt && (
              <Button type="link" size="small" style={{ padding: '0 6px' }} onClick={() => revert(r.version)}>
                Revert to this
              </Button>
            )}
          </Space>
        ),
      },
    ],
    [publish, revert],
  );

  if (unavailable) {
    return (
      <EmptyState
        icon={<BgColorsOutlined />}
        title="White-label theming"
        description="Brand the end-customer data room and its emails in your customer's identity. This server does not serve the theming API yet."
        bullets={[
          'A versioned theme ledger: draft, publish (one live theme per hub), revert',
          'Closed, sandboxed authoring: 20 palette tokens, layout variant, allowlisted CSS, brand assets',
          'The analyst console itself is never themed — theming is for the customer-facing plane',
        ]}
      />
    );
  }
  if (forbidden) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Theming is not readable with your role"
        description="theme.read is granted to hub admins, hub operators and auditors. Ask a hub admin if you need access — the server enforces this regardless of what the console shows."
      />
    );
  }

  return (
    <div>
      <div style={{ maxWidth: 860, marginBottom: 12 }}>
        <Title level={5} style={{ margin: 0 }}>
          <BgColorsOutlined style={{ marginRight: 8, color: 'var(--ol-primary)' }} />
          Branding / Theme — the end-customer plane
        </Title>
        <Paragraph type="secondary" style={{ margin: '4px 0 0', fontSize: 13 }}>
          Themes here brand what YOUR customer&rsquo;s end-customers see — the data room and its
          invitation emails — never this console. One live theme per hub; every change is a new
          ledger version (draft → publish → revert), and published versions are frozen with a
          content hash. Authoring is a closed, sandboxed surface: exactly the 20 palette tokens,
          three layout variants, an allowlisted CSS lane, and a fixed brand key set — the server
          refuses anything outside them with a precise reason. Writes need <Text code>theme.write</Text>{' '}
          (hub admin); reads are enforced by <Text code>theme.read</Text>.
        </Paragraph>
      </div>

      {live ? (
        <Descriptions
          size="small"
          column={3}
          bordered
          style={{ maxWidth: 860, marginBottom: 16 }}
          title={
            <span style={{ fontSize: 13.5 }}>
              Live theme <StatusTag status="PUBLISHED" />
            </span>
          }
        >
          <Descriptions.Item label="Version">{live.version}</Descriptions.Item>
          <Descriptions.Item label="Layout">{live.layoutVariant}</Descriptions.Item>
          <Descriptions.Item label="Product name">
            {typeof live.brand?.productName === 'string' && live.brand.productName ? (
              String(live.brand.productName)
            ) : (
              <Text type="secondary">—</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Published">
            {formatAbsolute(live.publishedAt)} {live.publishedBy ? `· ${live.publishedBy}` : ''}
          </Descriptions.Item>
          <Descriptions.Item label="Content hash" span={2}>
            <span className="ol-mono" style={{ fontSize: 11 }}>
              {live.contentHash ?? '—'}
            </span>
          </Descriptions.Item>
        </Descriptions>
      ) : (
        !loading && (
          <Alert
            type="info"
            showIcon
            style={{ maxWidth: 860, marginBottom: 16 }}
            message="No theme is published — the hub uses the platform default"
            description="The data room and its emails render the built-in OneLattice identity. That is an honest, fully supported state, not a misconfiguration. Draft and publish a version below to white-label them."
          />
        )
      )}

      {live && (
        <div style={{ marginBottom: 16 }}>
          <Button danger size="small" onClick={() => revert(null)}>
            Un-theme (back to platform default)
          </Button>
        </div>
      )}

      <Title level={5} style={{ margin: '0 0 8px' }}>
        Version ledger
      </Title>
      <Table<ThemeVersionView>
        rowKey="version"
        size="small"
        loading={loading}
        columns={columns}
        dataSource={versions}
        pagination={false}
        style={{ maxWidth: 980, marginBottom: 20 }}
        locale={{ emptyText: 'No theme versions yet — draft the first one below.' }}
      />

      <DraftComposer onDrafted={load} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Draft composer — visual authoring over the API's closed sets (never JSON-as-front-door; the
// synced request the server sees is exactly what these fields build).
// ---------------------------------------------------------------------------------------------

function DraftComposer({ onDrafted }: { onDrafted: () => Promise<void> | void }) {
  const { message } = App.useApp();

  const [layout, setLayout] = useState<string>('CLASSIC');
  const [tokenMode, setTokenMode] = useState<TokenMode>('both');
  const [flat, setFlat] = useState<Record<string, string>>({});
  const [lightTokens, setLightTokens] = useState<Record<string, string>>({});
  const [darkTokens, setDarkTokens] = useState<Record<string, string>>({});
  const [css, setCss] = useState('');
  const [productName, setProductName] = useState('');
  const [footerLine, setFooterLine] = useState('');
  const [markAssetId, setMarkAssetId] = useState('');
  const [wordmarkAssetId, setWordmarkAssetId] = useState('');
  const [faviconAssetId, setFaviconAssetId] = useState('');
  const [emailHeaderHex, setEmailHeaderHex] = useState('');
  const [emailSignOff, setEmailSignOff] = useState('');
  const [emailMarkAssetId, setEmailMarkAssetId] = useState('');
  const [design, setDesign] = useState<DesignDraft>(EMPTY_DESIGN);
  const [submitting, setSubmitting] = useState(false);
  const [serverRefusal, setServerRefusal] = useState<string | null>(null);

  const prune = (m: Record<string, string>) =>
    Object.fromEntries(Object.entries(m).filter(([, v]) => v.trim() !== ''));

  const buildRequest = useCallback((): ThemeRequest => {
    const tokens =
      tokenMode === 'both'
        ? prune(flat)
        : {
            ...(Object.keys(prune(lightTokens)).length ? { light: prune(lightTokens) } : {}),
            ...(Object.keys(prune(darkTokens)).length ? { dark: prune(darkTokens) } : {}),
          };
    const email = {
      ...(emailHeaderHex.trim() ? { headerHex: emailHeaderHex.trim() } : {}),
      ...(emailSignOff.trim() ? { signOff: emailSignOff.trim() } : {}),
      ...(emailMarkAssetId.trim() ? { markAssetId: emailMarkAssetId.trim() } : {}),
    };
    const brand = {
      ...(productName.trim() ? { productName: productName.trim() } : {}),
      ...(footerLine.trim() ? { footerLine: footerLine.trim() } : {}),
      ...(markAssetId.trim() ? { markAssetId: markAssetId.trim() } : {}),
      ...(wordmarkAssetId.trim() ? { wordmarkAssetId: wordmarkAssetId.trim() } : {}),
      ...(faviconAssetId.trim() ? { faviconAssetId: faviconAssetId.trim() } : {}),
      ...(Object.keys(email).length ? { email } : {}),
      ...(designOf(design) ? { design: designOf(design) } : {}),
    };
    return {
      layoutVariant: layout,
      tokens: Object.keys(tokens).length ? tokens : undefined,
      css: css.trim() || undefined,
      brand: Object.keys(brand).length ? brand : undefined,
    };
  }, [
    css,
    darkTokens,
    design,
    emailHeaderHex,
    emailMarkAssetId,
    emailSignOff,
    faviconAssetId,
    flat,
    footerLine,
    layout,
    lightTokens,
    markAssetId,
    productName,
    tokenMode,
    wordmarkAssetId,
  ]);

  const submit = useCallback(async () => {
    setServerRefusal(null);
    setSubmitting(true);
    try {
      const view = await draftTheme(buildRequest());
      message.success(`Draft version ${view.version} created — publish it from the ledger when ready.`);
      await onDrafted();
    } catch (e) {
      const n = normalizeApiError(e);
      // The server's closed-set refusals are precise and teaching — show them verbatim.
      setServerRefusal(n.message);
    } finally {
      setSubmitting(false);
    }
  }, [buildRequest, message, onDrafted]);

  const tokenEditor = (values: Record<string, string>, set: (v: Record<string, string>) => void) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
        gap: 8,
        maxWidth: 980,
      }}
    >
      {PALETTE_FIELDS.map((field) => (
        <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="ol-mono" style={{ fontSize: 11, width: 104, flexShrink: 0 }}>
            {field}
          </span>
          <Input
            size="small"
            placeholder="inherit"
            value={values[field] ?? ''}
            onChange={(e) => set({ ...values, [field]: e.target.value })}
            aria-label={`Palette token ${field}`}
          />
          <Swatch value={values[field] ?? ''} />
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ maxWidth: 980 }}>
      <Title level={5} style={{ margin: '0 0 4px' }}>
        New draft
      </Title>
      <Paragraph type="secondary" style={{ fontSize: 12.5, margin: '0 0 10px' }}>
        Every field is optional — an empty field means &ldquo;inherit the platform default&rdquo;.
        Saving creates a new DRAFT ledger version; nothing goes live until you publish it.
      </Paragraph>

      {serverRefusal && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={() => setServerRefusal(null)}
          style={{ marginBottom: 12 }}
          message="The server refused this draft"
          description={serverRefusal}
        />
      )}

      <Collapse
        defaultActiveKey={['layout-tokens']}
        style={{ marginBottom: 12 }}
        items={[
          {
            key: 'layout-tokens',
            label: 'Layout & palette tokens (the closed 20-field set)',
            children: (
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Space size={12} wrap>
                  <span>
                    <Text strong style={{ fontSize: 12.5, marginRight: 8 }}>
                      Layout variant
                    </Text>
                    <Select
                      size="small"
                      value={layout}
                      onChange={setLayout}
                      style={{ width: 140 }}
                      options={THEME_LAYOUTS.map((l) => ({ value: l, label: l }))}
                      aria-label="Layout variant"
                    />
                  </span>
                  <span>
                    <Text strong style={{ fontSize: 12.5, marginRight: 8 }}>
                      Token modes
                    </Text>
                    <Segmented
                      size="small"
                      value={tokenMode}
                      onChange={(v) => setTokenMode(v as TokenMode)}
                      options={[
                        { value: 'both', label: 'Same for light & dark' },
                        { value: 'light', label: 'Light overrides' },
                        { value: 'dark', label: 'Dark overrides' },
                      ]}
                    />
                  </span>
                </Space>
                <Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
                  Values are colour-like literals (e.g. <Text code>#0a5c36</Text>,{' '}
                  <Text code>rgb(10, 92, 54)</Text>). The swatch is a console-side hint; the data
                  room is the authoritative render, and the server refuses anything that is not a
                  colour-like literal or is outside these 20 fields.
                </Paragraph>
                {tokenMode === 'both' && tokenEditor(flat, setFlat)}
                {tokenMode === 'light' && tokenEditor(lightTokens, setLightTokens)}
                {tokenMode === 'dark' && tokenEditor(darkTokens, setDarkTokens)}
              </Space>
            ),
          },
          {
            key: 'design',
            label: 'Fonts and shape',
            children: <DesignFields value={design} onChange={setDesign} />,
          },
          {
            key: 'css',
            label: 'Custom CSS (sandboxed)',
            children: (
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
                  Use the palette and the fonts and shape section first. This is for what they do
                  not name. A closed allowlist of colour / spacing / typography / border / shadow
                  properties over plain <Text code>selector {'{ prop: value; }'}</Text> blocks.{' '}
                  <Text code>url()</Text> and every other function that can load something,{' '}
                  <Text code>@import</Text> and every other at-rule, attribute selectors, comments,
                  and HTML are refused with a precise reason at save AND re-checked at render. So is
                  anything that hides text, and any selector naming the consent text, which keeps
                  its own fixed style and the system font. The sandbox never silently
                  &ldquo;cleans&rdquo; your CSS, it accepts it whole or refuses it whole. Max 20,000
                  characters.
                </Paragraph>
                <Input.TextArea
                  rows={6}
                  value={css}
                  onChange={(e) => setCss(e.target.value)}
                  placeholder={'.dr-header { background: #0a5c36; }'}
                  className="ol-mono"
                  aria-label="Custom CSS"
                />
              </Space>
            ),
          },
          {
            key: 'brand',
            label: 'Brand — name, footer, logos, email header',
            children: (
              <Space direction="vertical" size={10} style={{ width: '100%' }}>
                <Space size={10} wrap>
                  <span>
                    <Text strong style={{ fontSize: 12.5, display: 'block' }}>
                      Product name <Text type="secondary">(≤80 chars)</Text>
                    </Text>
                    <Input
                      size="small"
                      style={{ width: 260 }}
                      maxLength={80}
                      value={productName}
                      onChange={(e) => setProductName(e.target.value)}
                      placeholder="Shown as the room's title"
                    />
                  </span>
                  <span>
                    <Text strong style={{ fontSize: 12.5, display: 'block' }}>
                      Footer line <Text type="secondary">(≤160 chars)</Text>
                    </Text>
                    <Input
                      size="small"
                      style={{ width: 340 }}
                      maxLength={160}
                      value={footerLine}
                      onChange={(e) => setFooterLine(e.target.value)}
                      placeholder="e.g. © Acme Bank — regulated by …"
                    />
                  </span>
                </Space>
                <AssetField label="Mark (square logo)" kind="MARK" value={markAssetId} onChange={setMarkAssetId} />
                <AssetField label="Wordmark" kind="WORDMARK" value={wordmarkAssetId} onChange={setWordmarkAssetId} />
                <AssetField label="Favicon" kind="FAVICON" value={faviconAssetId} onChange={setFaviconAssetId} />
                <div>
                  <Text strong style={{ fontSize: 12.5 }}>
                    Branded email (invitations)
                  </Text>
                  <Space size={10} wrap style={{ marginTop: 6 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Text style={{ fontSize: 12 }}>Header colour</Text>
                      <Input
                        size="small"
                        style={{ width: 120 }}
                        value={emailHeaderHex}
                        onChange={(e) => setEmailHeaderHex(e.target.value)}
                        placeholder="#0a5c36"
                      />
                      <Swatch value={emailHeaderHex} />
                    </span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Text style={{ fontSize: 12 }}>Sign-off</Text>
                      <Input
                        size="small"
                        style={{ width: 220 }}
                        value={emailSignOff}
                        onChange={(e) => setEmailSignOff(e.target.value)}
                        placeholder="The Acme onboarding team"
                      />
                    </span>
                  </Space>
                  <div style={{ marginTop: 6 }}>
                    <AssetField
                      label="Email mark"
                      kind="EMAIL_MARK"
                      value={emailMarkAssetId}
                      onChange={setEmailMarkAssetId}
                    />
                  </div>
                </div>
              </Space>
            ),
          },
        ]}
      />

      <Button type="primary" loading={submitting} onClick={() => void submit()}>
        Save as draft version
      </Button>
    </div>
  );
}

interface DesignDraft {
  family: string;
  assetId: string;
  useForBody: boolean;
  useForHeadings: boolean;
  radius: string;
}
const EMPTY_DESIGN: DesignDraft = { family: '', assetId: '', useForBody: true, useForHeadings: true, radius: 'DEFAULT' };

/** The `brand.design` block, or null when nothing was set. The server validates all of it. */
function designOf(d: DesignDraft): Record<string, unknown> | null {
  const font = d.family.trim() && d.assetId;
  if (!font && d.radius === 'DEFAULT') return null;
  return {
    ...(font
      ? {
          fonts: [{ family: d.family.trim(), assetId: d.assetId }],
          ...(d.useForBody ? { bodyFamily: d.family.trim() } : {}),
          ...(d.useForHeadings ? { headingFamily: d.family.trim() } : {}),
        }
      : {}),
    ...(d.radius !== 'DEFAULT' ? { radius: d.radius } : {}),
  };
}

/** One brand font and the corner radius (OL-23). Lists come from the server. */
function DesignFields({ value, onChange }: { value: DesignDraft; onChange: (next: DesignDraft) => void }) {
  const { message } = App.useApp();
  const [vocabulary, setVocabulary] = useState<ThemeVocabulary | null>(null);
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    let current = true;
    getThemeVocabulary()
      .then((v) => {
        if (current) setVocabulary(v);
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, []);
  return (
    <Space direction="vertical" size={10} style={{ width: '100%' }} data-testid="theme-design">
      <Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
        Your font applies across the room, with the platform&rsquo;s fonts behind it for scripts it
        does not cover. It never applies to consent text, which stays in the system font so that the
        words shown are the words on record. Fonts are checked against what the file really is.
      </Paragraph>
      <Space size={10} wrap>
        <span>
          <Text strong style={{ fontSize: 12.5, display: 'block' }}>
            Font family name
          </Text>
          <Input
            size="small"
            style={{ width: 240 }}
            aria-label="Font family name"
            maxLength={60}
            value={value.family}
            onChange={(e) => onChange({ ...value, family: e.target.value })}
            placeholder="Acme Sans"
          />
        </span>
        <span>
          <Text strong style={{ fontSize: 12.5, display: 'block' }}>
            Corners
          </Text>
          <Select
            size="small"
            style={{ width: 140 }}
            aria-label="Corner radius"
            data-testid="theme-radius"
            value={value.radius}
            options={(vocabulary?.radii ?? ['DEFAULT']).map((r) => ({ value: r, label: r }))}
            onChange={(radius: string) => onChange({ ...value, radius })}
          />
        </span>
      </Space>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Upload
          accept=".woff2,.woff,.ttf,.otf"
          showUploadList={false}
          beforeUpload={(file) => {
            if (vocabulary && file.size > vocabulary.maxFontBytes) {
              message.error(`Fonts are capped at ${Math.round(vocabulary.maxFontBytes / 1048576)} MiB.`);
              return Upload.LIST_IGNORE;
            }
            setUploading(true);
            uploadThemeAsset('FONT', asFontFile(file))
              .then((asset) => {
                onChange({ ...value, assetId: asset.assetId });
                message.success(`Font uploaded (${asset.mediaType}, ${asset.sizeBytes} bytes).`);
              })
              .catch((e) => message.error(normalizeApiError(e).message))
              .finally(() => setUploading(false));
            return Upload.LIST_IGNORE;
          }}
        >
          <Button size="small" icon={<UploadOutlined />} loading={uploading} data-testid="theme-font-upload">
            Upload a font
          </Button>
        </Upload>
        {value.assetId && (
          <span className="ol-mono" style={{ fontSize: 11 }} data-testid="theme-font-asset">
            {value.assetId}
          </span>
        )}
        <Checkbox checked={value.useForBody} onChange={(e) => onChange({ ...value, useForBody: e.target.checked })}>
          Body text
        </Checkbox>
        <Checkbox
          checked={value.useForHeadings}
          onChange={(e) => onChange({ ...value, useForHeadings: e.target.checked })}
        >
          Headings
        </Checkbox>
      </div>
    </Space>
  );
}

/** One brand-asset slot: upload (≤1 MiB, png/svg/jpeg — SVGs are server-sanitised) sets the id. */
function AssetField({
  label,
  kind,
  value,
  onChange,
}: {
  label: string;
  kind: (typeof THEME_ASSET_KINDS)[number];
  value: string;
  onChange: (assetId: string) => void;
}) {
  const { message } = App.useApp();
  const [uploading, setUploading] = useState(false);

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <Text style={{ fontSize: 12.5, width: 140 }}>{label}</Text>
      <Upload
        accept={THEME_ASSET_MEDIA_TYPES.join(',')}
        showUploadList={false}
        beforeUpload={(file) => {
          if (file.size > THEME_ASSET_MAX_BYTES) {
            message.error('Brand assets are capped at 1 MiB.');
            return Upload.LIST_IGNORE;
          }
          setUploading(true);
          void uploadThemeAsset(kind, file)
            .then((asset) => {
              onChange(asset.assetId);
              message.success(`${label} uploaded (${asset.mediaType}, ${asset.sizeBytes} bytes).`);
            })
            .catch((e) => message.error(normalizeApiError(e).message))
            .finally(() => setUploading(false));
          return false; // we handled the transport ourselves
        }}
      >
        <Button size="small" icon={<UploadOutlined />} loading={uploading}>
          Upload
        </Button>
      </Upload>
      {value ? (
        <Tooltip title="The uploaded asset's id — referenced by the draft; stored hub-scoped server-side.">
          <span className="ol-mono" style={{ fontSize: 11 }}>
            {value}
          </span>
        </Tooltip>
      ) : (
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          none — the platform default is used
        </Text>
      )}
      {value && (
        <Button size="small" type="link" style={{ padding: 0 }} onClick={() => onChange('')}>
          clear
        </Button>
      )}
    </span>
  );
}
